import { z } from 'zod'
import { productSummarySchema } from './catalog.ts'

/**
 * The three things a returning customer should never have to do twice: retype
 * where the goods go, rebuild last month's basket, and rebuild the basket they use
 * every week.
 *
 * All of it is scoped to the customer as a party — their business, or themselves
 * when they have none (D15) — so the counter PC and the site phone see the same
 * addresses and the same saved lists.
 */

export const deliveryAddressSchema = z.object({
  id: z.string(),
  label: z.string(),
  addressLine: z.string(),
  city: z.string(),
  contactName: z.string().nullable(),
  contactPhone: z.string().nullable(),
  isDefault: z.boolean(),
})
export type DeliveryAddress = z.infer<typeof deliveryAddressSchema>

export const saveAddressSchema = z.object({
  label: z.string().trim().min(1, 'Give it a name, like "Site" or "Yard".').max(60),
  addressLine: z.string().trim().min(1, 'Enter the address.').max(200),
  city: z.string().trim().min(1, 'Enter the city.').max(80),
  contactName: z.string().trim().max(80).nullish(),
  contactPhone: z.string().trim().max(20).nullish(),
  isDefault: z.boolean().default(false),
})
export type SaveAddressInput = z.infer<typeof saveAddressSchema>

export const savedBasketSchema = z.object({
  id: z.string(),
  name: z.string(),
  lines: z.array(
    z.object({
      productId: z.string(),
      product: productSummarySchema,
      qty: z.number().int(),
    }),
  ),
  updatedAt: z.number().int(),
})
export type SavedBasket = z.infer<typeof savedBasketSchema>

export const saveBasketSchema = z.object({
  name: z.string().trim().min(1, 'Give the list a name.').max(60),
})
export type SaveBasketInput = z.infer<typeof saveBasketSchema>

/** How a saved list or a past order lands in the basket. */
export const RESTORE_MODES = ['replace', 'add'] as const
export type RestoreMode = (typeof RESTORE_MODES)[number]

export const restoreBasketSchema = z.object({
  mode: z.enum(RESTORE_MODES).default('replace'),
})
export type RestoreBasketInput = z.infer<typeof restoreBasketSchema>

/**
 * What came back from a reorder. Items can be delisted between one order and the
 * next, and silently dropping them is how somebody discovers at the yard that half
 * the load was never ordered.
 */
export const restoreResultSchema = z.object({
  added: z.number().int(),
  /** Named, not counted — the customer needs to know which ones to ask about. */
  unavailable: z.array(z.string()),
})
export type RestoreResult = z.infer<typeof restoreResultSchema>
